import * as THREE from "three";

// Animation LOD for crowds of figures (the suggestion "animation LOD: stepped poses, posing only when seen, staggered
// clocks", characters-animation-faces.md; spike 002 gate D1.5). Posing a figure (its motion's gait, look-at and
// chains, its pose layer) is the per-character cost that grows with a crowd, so a figure is posed less the less it
// shows:
//   - bands by distance: near figures pose every frame, farther ones every 2nd, 4th… frame (a stepped clock: the pose
//     holds between its steps while the root still moves every frame), and each band says whether it casts a shadow;
//   - only when seen: a figure outside the view is not posed at all (or every Nth frame, if the game asks), and one
//     that comes into view is posed on that frame, so a stale pose never shows;
//   - staggered: each figure's stepped clock is offset by a hash of its id, so a band's work spreads across frames
//     instead of landing on one;
//   - beyond the last band a figure is hidden (not drawn, not posed).
// Each pose gets the time since the figure was last posed (capped), so its motion moves by the time that passed.
// Counters report items, poses and skips per frame and since a reset, by band and on or off screen: the check that
// far and unseen figures are posed less. The game measures distance and visibility (or uses `viewTest`), calls
// `plan` once a frame, and poses the figures it says to.

/** Planted defects beside what they break: a game's pose-counter check must go red with them on. */
export const LOD_FAULTS = {
  /** Figures out of view are posed every frame, as if seen (gate D1.5's planted defect). */
  offscreenEveryTick: false,
};

/** A distance band: figures nearer than `maxDistance` (m) pose every `every` frames and cast shadows or not. */
export interface LodBand {
  id: string;
  maxDistance: number;
  /** 1: every frame. */
  every: number;
  shadows: boolean;
}

/** The game's numbers. */
export interface AnimationLodOptions {
  /** Nearest first. Past the last band's distance a figure is hidden. */
  bands: readonly LodBand[];
  /** Figures out of view pose every `every` frames (0: never while unseen). */
  offscreen: { every: number };
  /** The most time one pose may catch up on (s): a figure posed after a long gap moves by at most this. Default 0.25. */
  maxCatchUp?: number;
}

/** One figure this frame. */
export interface LodItem {
  id: string;
  /** Metres from the camera. */
  distance: number;
  /** In the view (its bounds meet the camera's frustum). */
  visible: boolean;
}

/** What to do with one figure this frame. */
export interface LodPlan {
  id: string;
  /** Its band's id, "hidden" past the last band. */
  band: string;
  /** Pose it now, by `dt` seconds. */
  pose: boolean;
  dt: number;
  /** Draw it. */
  shown: boolean;
  shadows: boolean;
}

/** Counts per frame and since the last reset. */
export interface LodCounters {
  frames: number;
  /** Item-frames seen and poses done: on screen by band, and off screen. */
  seen: Record<string, number>;
  posed: Record<string, number>;
  offscreen: { seen: number; posed: number };
  hidden: number;
  /** The last frame's: figures, poses. */
  last: { items: number; posed: number; posedOffscreen: number };
  /** The most poses in one frame. */
  maxPerFrame: number;
}

/** A figure's stagger phase in a band of `every` frames: a stable hash of its id. */
export function staggerOf(id: string, every: number): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return every > 1 ? (h >>> 0) % every : 0;
}

/** The planner. */
export class AnimationLod {
  private readonly bands: LodBand[];
  private readonly pending = new Map<string, number>();
  private readonly wasVisible = new Map<string, boolean>();
  private c: LodCounters;

  constructor(readonly options: AnimationLodOptions) {
    if (!options.bands.length) throw new Error("@rpg/figure: animation LOD needs at least one band");
    this.bands = [...options.bands].sort((a, b) => a.maxDistance - b.maxDistance);
    for (const b of this.bands) if (!(b.every >= 1)) throw new Error(`@rpg/figure: band "${b.id}" must pose at least every frame (every >= 1)`);
    this.c = this.fresh();
  }

  private fresh(): LodCounters {
    const seen: Record<string, number> = {};
    const posed: Record<string, number> = {};
    for (const b of this.options.bands) {
      seen[b.id] = 0;
      posed[b.id] = 0;
    }
    return { frames: 0, seen, posed, offscreen: { seen: 0, posed: 0 }, hidden: 0, last: { items: 0, posed: 0, posedOffscreen: 0 }, maxPerFrame: 0 };
  }

  /** The band for a distance (null past the last). */
  bandAt(distance: number): LodBand | null {
    for (const b of this.bands) if (distance <= b.maxDistance) return b;
    return null;
  }

  /** Plan frame number `frame` (any counter the game keeps, +1 a frame) that took `dt` seconds. */
  plan(frame: number, dt: number, items: readonly LodItem[]): LodPlan[] {
    const cap = this.options.maxCatchUp ?? 0.25;
    const out: LodPlan[] = [];
    let posed = 0;
    let posedOff = 0;
    for (const it of items) {
      const band = this.bandAt(it.distance);
      const pend = Math.min(cap, (this.pending.get(it.id) ?? 0) + dt);
      if (!band) {
        this.pending.set(it.id, pend);
        this.wasVisible.set(it.id, false);
        this.c.hidden++;
        out.push({ id: it.id, band: "hidden", pose: false, dt: 0, shown: false, shadows: false });
        continue;
      }
      let every: number;
      if (it.visible || LOD_FAULTS.offscreenEveryTick) every = it.visible ? band.every : 1;
      else every = this.options.offscreen.every;
      const cameIntoView = it.visible && this.wasVisible.get(it.id) !== true;
      const pose = cameIntoView || (every > 0 && (frame + staggerOf(it.id, every)) % every === 0);
      this.wasVisible.set(it.id, it.visible);
      if (it.visible) this.c.seen[band.id] = (this.c.seen[band.id] ?? 0) + 1;
      else this.c.offscreen.seen++;
      if (pose) {
        this.pending.set(it.id, 0);
        posed++;
        if (it.visible) this.c.posed[band.id] = (this.c.posed[band.id] ?? 0) + 1;
        else {
          this.c.offscreen.posed++;
          posedOff++;
        }
      } else this.pending.set(it.id, pend);
      out.push({ id: it.id, band: band.id, pose, dt: pose ? pend : 0, shown: true, shadows: band.shadows });
    }
    this.c.frames++;
    this.c.last = { items: items.length, posed, posedOffscreen: posedOff };
    this.c.maxPerFrame = Math.max(this.c.maxPerFrame, posed);
    return out;
  }

  /** Forget a figure (it left the scene). */
  forget(id: string): void {
    this.pending.delete(id);
    this.wasVisible.delete(id);
  }

  counters(): LodCounters {
    return structuredClone(this.c);
  }

  resetCounters(): void {
    this.c = this.fresh();
  }
}

/** A visibility test against a camera's frustum this frame: whether a sphere (centre, radius) meets it. */
export function viewTest(camera: THREE.Camera): (centre: { x: number; y: number; z: number }, radius: number) => boolean {
  camera.updateMatrixWorld();
  const m = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  const f = new THREE.Frustum().setFromProjectionMatrix(m);
  const s = new THREE.Sphere();
  return (c, r) => f.intersectsSphere(s.set(new THREE.Vector3(c.x, c.y, c.z), r));
}
